/**
 * Compass overlay (HEX-124 / HEX-142 / ADR-0018 §6).
 *
 * A labelled schematic dial in the bottom-right corner pointing toward the
 * nearest unliberated town, with a live hex-distance readout. When no
 * unliberated town is in range it drops to a dim resting state ("no target in
 * range") rather than vanishing (HEX-134) — the dial stays on screen so its
 * absence never reads as a missing HUD element.
 *
 * HEX-142: the earlier 52px unlabelled dial was invisible against the dark map.
 * The dial now sits in a `--line-soft` panel with a persistent COMPASS label and
 * a distance value, so it reads as a deliberate instrument. ADR-0014 style — no
 * assets, pure geometry + theme tokens.
 */

import { compassAngle } from './compass';

const NS = 'http://www.w3.org/2000/svg';

const LABEL_ACTIVE = 'Compass to nearest unliberated town';
const LABEL_RESTING = 'Compass — no unliberated town in range';
const READOUT_RESTING = 'no target';

/** Cube distance between two axial hexes — matches `nearestUnliberatedTown`. */
function hexDistance(aq: number, ar: number, bq: number, br: number): number {
  const dq = bq - aq;
  const dr = br - ar;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

export class CompassHud {
  private root: HTMLDivElement | null = null;
  private needleGroup: SVGGElement | null = null;
  private restMarker: SVGCircleElement | null = null;
  private readoutEl: HTMLSpanElement | null = null;
  /** Tracks the painted state so the DOM is only touched on a transition. */
  private state: 'active' | 'resting' | null = null;

  constructor(private readonly container: HTMLElement) {}

  mount(): void {
    if (this.root) return;

    const root = document.createElement('div');
    root.className = 'hex6-compass-hud';
    // Panel chrome matches the other HUD clusters so the dial reads as a
    // deliberate instrument rather than a stray mark in the corner.
    root.style.cssText =
      'position:absolute;bottom:16px;right:16px;z-index:4;pointer-events:none;' +
      'display:flex;flex-direction:column;align-items:center;gap:4px;' +
      'padding:8px 10px 6px;border-radius:6px;' +
      'background:rgba(13,18,16,0.9);border:1px solid var(--line-soft);' +
      'font:10px/1.3 var(--f-mono);letter-spacing:0.14em;color:var(--bone);';

    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '-26 -26 52 52');
    svg.setAttribute('width', '60');
    svg.setAttribute('height', '60');
    svg.style.display = 'block';

    // Dial ring — brighter rim than before so the circle is legible on dark map.
    const ring = document.createElementNS(NS, 'circle');
    ring.setAttribute('cx', '0');
    ring.setAttribute('cy', '0');
    ring.setAttribute('r', '22');
    ring.setAttribute('fill', 'rgba(13,18,16,0.82)');
    ring.setAttribute('stroke', '#6f6a55');
    ring.setAttribute('stroke-width', '1.5');
    svg.appendChild(ring);

    // Cardinal tick at top (north)
    const tick = document.createElementNS(NS, 'line');
    tick.setAttribute('x1', '0');
    tick.setAttribute('y1', '-17');
    tick.setAttribute('x2', '0');
    tick.setAttribute('y2', '-21');
    tick.setAttribute('stroke', '#5c5848');
    tick.setAttribute('stroke-width', '1.5');
    svg.appendChild(tick);

    // Resting marker — a dim hollow centre dot shown when no town is in range.
    // Reads as "compass present, idle" without implying a heading.
    const rest = document.createElementNS(NS, 'circle');
    rest.setAttribute('cx', '0');
    rest.setAttribute('cy', '0');
    rest.setAttribute('r', '4');
    rest.setAttribute('fill', 'none');
    rest.setAttribute('stroke', '#847f6c');
    rest.setAttribute('stroke-width', '1.5');
    svg.appendChild(rest);

    // Needle group — rotated on update
    const g = document.createElementNS(NS, 'g');

    // Forward arrow (gold, toward target). A thin dark outline lifts it off the
    // dial face for the active-contrast bump (HEX-142).
    const fwd = document.createElementNS(NS, 'polygon');
    fwd.setAttribute('points', '0,-16 4,3.5 0,0 -4,3.5');
    fwd.setAttribute('fill', '#e6b450');
    fwd.setAttribute('stroke', 'rgba(13,18,16,0.85)');
    fwd.setAttribute('stroke-width', '0.6');
    g.appendChild(fwd);

    // Rear stub (dim)
    const rear = document.createElementNS(NS, 'polygon');
    rear.setAttribute('points', '0,5 2,9 -2,9');
    rear.setAttribute('fill', '#847f6c');
    g.appendChild(rear);

    // Centre pip
    const pip = document.createElementNS(NS, 'circle');
    pip.setAttribute('cx', '0');
    pip.setAttribute('cy', '0');
    pip.setAttribute('r', '2.5');
    pip.setAttribute('fill', '#ddd2b8');
    g.appendChild(pip);

    svg.appendChild(g);
    root.appendChild(svg);

    // Persistent label — present in both states so the corner reads as "COMPASS".
    const label = document.createElement('span');
    label.className = 'hex6-compass-hud__label';
    label.textContent = 'COMPASS';
    label.style.cssText = 'text-transform:uppercase;opacity:0.65;';
    root.appendChild(label);

    // Live readout — hex distance to target when active, resting copy otherwise.
    const readout = document.createElement('span');
    readout.className = 'hex6-compass-hud__readout';
    readout.style.cssText = 'font-size:12px;letter-spacing:0.04em;color:var(--gold);';
    root.appendChild(readout);

    this.container.appendChild(root);
    this.root = root;
    this.needleGroup = g;
    this.restMarker = rest;
    this.readoutEl = readout;

    // Start resting until the first update points us at a town.
    this.enterResting();
  }

  /**
   * Repoint the compass. Pass `null` to drop to the resting "no target in range"
   * state (e.g. every nearby town liberated).
   */
  update(target: { q: number; r: number } | null, pq: number, pr: number): void {
    if (!this.root) return;
    if (target === null) {
      this.enterResting();
      return;
    }
    this.enterActive();
    const deg = (compassAngle(pq, pr, target.q, target.r) * 180) / Math.PI;
    this.needleGroup?.setAttribute('transform', `rotate(${deg.toFixed(1)})`);
    const d = hexDistance(pq, pr, target.q, target.r);
    if (this.readoutEl) this.readoutEl.textContent = `${d} hex`;
  }

  private enterActive(): void {
    if (this.state === 'active') return;
    this.state = 'active';
    if (this.needleGroup) this.needleGroup.style.display = '';
    if (this.restMarker) this.restMarker.style.display = 'none';
    if (this.readoutEl) this.readoutEl.style.color = 'var(--gold)';
    this.root?.style.setProperty('opacity', '1');
    this.root?.setAttribute('aria-label', LABEL_ACTIVE);
  }

  private enterResting(): void {
    if (this.state === 'resting') return;
    this.state = 'resting';
    if (this.needleGroup) this.needleGroup.style.display = 'none';
    if (this.restMarker) this.restMarker.style.display = '';
    if (this.readoutEl) {
      this.readoutEl.textContent = READOUT_RESTING;
      this.readoutEl.style.color = 'var(--bone-dim)';
    }
    this.root?.style.setProperty('opacity', '0.5');
    this.root?.setAttribute('aria-label', LABEL_RESTING);
  }

  destroy(): void {
    this.root?.remove();
    this.root = null;
    this.needleGroup = null;
    this.restMarker = null;
    this.readoutEl = null;
    this.state = null;
  }
}
